import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Container, Card, Grid, Dropdown, Checkbox, Select, Button, Icon} from 'semantic-ui-react';
import {hasAccessPermission} from '../../../common/permissions/validations';
import {scopes, resources} from '../../../common/permissions/dictionary';
import {paths} from '../../../common/dictionaries/urls';
import {Actions} from '../../../common/actions';
import {getDropdownOptions} from '../../../common/util/dropdown';
import WtsEmployeeModalGestor from './employeeModalGestor';
import CommercialTable from '../../../common/components/commercialTable/commercialTable';
import './styles.scss';
import WtsSkillsTechDetailsModal from './skillsTechDetailsModal';
import {DefaultColumnFilter} from '../../../manageOrders/components/internalCommons/TableFilters';
import {renderTableLoader} from '../../../common/components/tableLoader';
import moment from 'moment';

const {
  navigateTo,
  getWtsAreas,
  changeWtsAreaSelected,
  updateWtsEmployeeStatus,
  getWtsEmployeesByRuc,
  getWtsDealer,
  getWtsAgenciesDealer,
} = Actions;

const SortIcon = ({direction}) => {
  if (!direction) {
    return <span style={{'marginLeft': '5px', 'opacity': '0.4', 'fontSize': '1.2em'}}>⇅</span>;
  }
  return <span style={{'marginLeft': '5px', 'fontSize': '1.2em'}}>{direction === 'asc' ? '↑' : '↓'}</span>;
};

const exactTextFilter = (rows, id, filterValue) => {
  if (filterValue === '' || !filterValue) {
    return rows;
  }
  const colId = Array.isArray(id) ? id[0] : id;
  return rows.filter((row) => {
    const val = row.values[colId];
    if (Array.isArray(val)) {
      return val.includes(filterValue);
    }
    return val === filterValue;
  });
};

// eslint-disable-next-line max-statements
const sortEmployeesFn = (a, b, sortKey, sortDir) => {
  let valA = a[sortKey];
  let valB = b[sortKey];
  if (Array.isArray(valA)) {
    valA = valA[0] || '';
  }
  if (Array.isArray(valB)) {
    valB = valB[0] || '';
  }
  if (['startDate'].includes(sortKey)) {
    valA = valA ? moment(valA).valueOf() : 0;
    valB = valB ? moment(valB).valueOf() : 0;
    return sortDir === 'asc' ? valA - valB : valB - valA;
  }
  const numA = parseFloat(valA);
  const numB = parseFloat(valB);
  if (!isNaN(numA) && !isNaN(numB)) {
    return sortDir === 'asc' ? numA - numB : numB - numA;
  }
  const strA = (valA || '').toString().toLowerCase();
  const strB = (valB || '').toString().toLowerCase();
  if (strA < strB) {
    return sortDir === 'asc' ? -1 : 1;
  }
  if (strA > strB) {
    return sortDir === 'asc' ? 1 : -1;
  }
  return 0;
};

const ExcelColumnFilter = ({'column': {filterValue, setFilter, preFilteredRows, id}}) => {
  const options = React.useMemo(() => {
    const opts = new Set();
    const colId = Array.isArray(id) ? id[0] : id;
    preFilteredRows.forEach((row) => {
      const val = row.values[colId];
      if (val !== null && typeof val !== 'undefined' && val !== '') {
        if (Array.isArray(val)) {
          val.forEach((v) => {
            if (v !== null && typeof v !== 'undefined' && v !== '') {
              opts.add(v);
            }
          });
        } else {
          opts.add(val);
        }
      }
    });
    return [...opts.values()].sort();
  }, [id, preFilteredRows]);

  const dropdownOptions = [
    {'key': 'all', 'value': '', 'text': 'TODOS'},
    ...options.map((opt, i) => ({'key': i, 'value': opt, 'text': opt})),
  ];

  return (
    <Select
      compact
      search
      placeholder="TODOS"
      value={filterValue || ''}
      options={dropdownOptions}
      style={{'minWidth': '120px', 'width': '100%'}}
      onChange={(e, {value}) => setFilter(value || '')}
    />
  );
};

const SelectColumnFilter = ({'column': {filterValue, setFilter}}) => {
  useEffect(() => {
    if (typeof filterValue === 'undefined') {
      setFilter('ACTIVO');
    }
  }, [filterValue, setFilter]);

  const options = [
    {'key': 'all', 'value': 'TODOS', 'text': 'TODOS'},
    {'key': 'active', 'value': 'ACTIVO', 'text': 'ACTIVO'},
    {'key': 'inactive', 'value': 'INACTIVO', 'text': 'INACTIVO'},
  ];

  return (
    <Select
      compact
      placeholder="ACTIVO"
      value={typeof filterValue !== 'undefined' ? filterValue : 'ACTIVO'}
      options={options}
      style={{'minWidth': '120px', 'width': '100%'}}
      onChange={(e, {value}) => setFilter(value)}
    />
  );
};

// eslint-disable-next-line max-statements
const WtsEmployeesGestor = () => {
  const permissions = useSelector((state) => state.session?.permissions || {});
  const currentUser = useSelector((state) => state.session || {});
  const areas = useSelector((state) => state.wtsAreas?.areas || []);
  const areaSelected = useSelector((state) => state.wtsAreas?.areaSelected);
  const wtsEmployees = useSelector((state) => state.wtsEmployees?.employeesByRuc || []);
  const loading = useSelector((state) => state.wtsEmployees?.loading);
  const wtsAgencies = useSelector((state) => state.wtsAgencies?.agencies || []);

  const [tableState, setTableState] = useState({
    'filteredCount': 0,
    'sortKey': null,
    'sortDir': null,
    'key': 0,
  });

  const dispatch = useDispatch();

  const userDealerId = currentUser.dealerId || currentUser.ruc || permissions.dealerId || permissions.ruc;

  useEffect(() => {
    if (!hasAccessPermission(permissions, resources.WtsEmpleadosGestor, scopes.See) &&
      !hasAccessPermission(permissions, resources.WtsEmpleadosGestor, scopes.Edit)) {
      dispatch(navigateTo(paths.AccessDenied));
      return;
    }
    dispatch(getWtsAreas());
    dispatch(changeWtsAreaSelected(1));
  }, [dispatch, permissions]);

  useEffect(() => {
    if (userDealerId && typeof getWtsEmployeesByRuc === 'function') {
      dispatch(getWtsEmployeesByRuc(userDealerId));
    }
    if (areaSelected?.id) {
      dispatch(getWtsDealer(areaSelected.id));
      if (userDealerId && typeof getWtsAgenciesDealer === 'function') {
        dispatch(getWtsAgenciesDealer(areaSelected.id, userDealerId));
      }
    }
  }, [dispatch, userDealerId, areaSelected?.id]);

  const onChangeWtsAreaSelected = (e, {value}) => {
    e.preventDefault();
    const selected = (areas || []).find((x) => x.id === value);
    dispatch(changeWtsAreaSelected(selected));
  };

  const updateStatus = async(e, checkedValue, item) => {
    e.preventDefault();
    const payload = {...item, 'status': checkedValue ? 'ACTIVO' : 'INACTIVO'};
    if (typeof updateWtsEmployeeStatus === 'function') {
      await dispatch(updateWtsEmployeeStatus(areaSelected?.id || 1, {'data': payload}));
      if (userDealerId && typeof getWtsEmployeesByRuc === 'function') {
        dispatch(getWtsEmployeesByRuc(userDealerId));
      }
    }
  };

  const nextEmployeeId = React.useMemo(() => {
    if (!wtsEmployees || wtsEmployees.length === 0) {
      return 1;
    }
    const ids = wtsEmployees.map((emp) => parseInt(emp.id, 10)).filter((id) => !isNaN(id));
    return ids.length > 0 ? Math.max(...ids) + 1 : 1;
  }, [wtsEmployees]);

  const handleSort = (accessor) => {
    setTableState((prev) => {
      if (prev.sortKey === accessor) {
        if (prev.sortDir === 'asc') {
          return {...prev, 'sortKey': accessor, 'sortDir': 'desc'};
        }
        if (prev.sortDir === 'desc') {
          return {...prev, 'sortKey': null, 'sortDir': null};
        }
      }
      return {...prev, 'sortKey': accessor, 'sortDir': 'asc'};
    });
  };

  const sortedEmployees = React.useMemo(() => {
    const mappedEmployees = wtsEmployees.map((emp) => {
      const matchedAg = (wtsAgencies || []).find((a) => a.id === emp.agencyCode);
      const allCerts = [...(emp.certificatesTmc || []), ...(emp.certificatesTde || [])]
        .sort((a, b) => {
          const dateA = a.date ? moment(a.date).valueOf() : 0;
          const dateB = b.date ? moment(b.date).valueOf() : 0;
          return dateB - dateA;
        })
        .map((c) => c.certificate || '')
        .filter(Boolean);

      return {
        ...emp,
        'agencyName': matchedAg?.agency || emp.agencyCode || emp.agency,
        'allCertificates': allCerts,
      };
    });

    if (!tableState.sortKey || !tableState.sortDir) {
      return mappedEmployees;
    }
    return [...mappedEmployees].sort((a, b) => sortEmployeesFn(a, b, tableState.sortKey, tableState.sortDir));
  }, [wtsEmployees, tableState.sortKey, tableState.sortDir, wtsAgencies]);

  const makeSortableHeader = (label, accessor) => () => (
    <span className="header-title" onClick={() => handleSort(accessor)}
      style={{'cursor': 'pointer', 'userSelect': 'none', 'whiteSpace': 'nowrap'}}>
      {label} <SortIcon direction={tableState.sortKey === accessor ? tableState.sortDir : null} />
    </span>
  );

  const columns = React.useMemo(() => [
    {
      'Header': makeSortableHeader('NO.', 'id'),
      'accessor': 'id',
    },
    {
      'Header': makeSortableHeader('CONCESIONARIO', 'dealerName'),
      'accessor': 'dealerName',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
    },
    {
      'Header': makeSortableHeader('AGENCIA', 'agencyName'),
      'accessor': 'agencyName',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
    },
    {
      'Header': makeSortableHeader('CÉDULA', 'document'),
      'accessor': 'document',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
    },
    {
      'Header': makeSortableHeader('NOMBRE', 'names'),
      'accessor': 'names',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
    },
    {
      'Header': makeSortableHeader('APELLIDO', 'lastNames'),
      'accessor': 'lastNames',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
    },
    {
      'Header': makeSortableHeader('CARGO', 'charge'),
      'accessor': 'charge',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
    },
    {
      'Header': makeSortableHeader('FECHA INGRESO', 'startDate'),
      'accessor': 'startDate',
      'Cell': ({value}) => (value ? moment(value).format('DD/MM/YYYY') : ''),
    },
    {
      'Header': makeSortableHeader('ÚLTIMO CERTIFICADO', 'allCertificates'),
      'accessor': 'allCertificates',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
      'Cell': ({value}) => (Array.isArray(value) && value.length > 0 ? value[0] : ''),
    },
    {
      'Header': makeSortableHeader('USUARIO', 'user'),
      'accessor': 'user',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
      'Cell': (item) => (
        <div>
          <div>{item.value || 'Sin asignar'}</div>
          <small style={{'color': 'gray'}}> {item.row?.original?.email || ''} </small>
        </div>
      ),
    },
    {
      'Header': makeSortableHeader('ESTADO', 'status'),
      'accessor': 'status',
      'Filter': SelectColumnFilter,
      'filter': (rows, id, filterValue) => {
        if (filterValue === 'TODOS' || typeof filterValue === 'undefined') {
          return rows;
        }
        const colId = Array.isArray(id) ? id[0] : id;
        return rows.filter((row) => row.values[colId] === filterValue);
      },
      'Cell': (item) => (
        <Checkbox
          toggle
          checked={item.value === 'ACTIVO'}
          className="green"
          onChange={(e, input) => updateStatus(e, input.checked, item.row?.original || {})}
        />
      ),
    },
    {
      'Header': () => (
        <span className="header-title" style={{'userSelect': 'none', 'whiteSpace': 'nowrap'}}>
          ACCIONES
        </span>
      ),
      'id': 'skills_actions',
      'width': 1,
      'disableFilters': true,
      'disableSortBy': true,
      'Cell': (item) => (
        <div className="flex center end">
          <WtsEmployeeModalGestor mode="edit" employeeToEdit={item.row?.original || {}} />
        </div>
      ),
    },
  ], [tableState.sortKey, tableState.sortDir, areaSelected, updateStatus]);

  return (
    <Container fluid>
      <Card raised fluid style={{'backgroundColor': 'transparent'}}>
        <div className="title-bar">
          <Grid>
            <Grid.Row>
              <Grid.Column>
                <div className="title">
                  <h2>Mantenimiento de Participantes (Gestor Dealer)</h2>
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row textAlign="left">
              <Grid.Column width={5}>
                <div className="ui fluid labeled input">
                  <div className="ui label label">Área de capacitación</div>
                  <Dropdown
                    fluid
                    selection
                    labeled
                    className="labeled-dropdown"
                    value={areaSelected?.id}
                    onChange={(e, i) => onChangeWtsAreaSelected(e, i)}
                    options={getDropdownOptions(areas || [], 'id', 'description')}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </Card>
      <Card raised fluid>
        {loading ? renderTableLoader() : (
          <Grid padded>
            <Grid.Row style={{'padding': 0}}>
              <Grid.Column textAlign="left" width={14}>
                <div style={{'display': 'flex', 'alignItems': 'center', 'gap': '14px'}}>
                  <WtsEmployeeModalGestor mode="new" nextId={nextEmployeeId} />
                  <div style={{
                    'display': 'inline-flex',
                    'alignItems': 'center',
                    'gap': '8px',
                    'backgroundColor': '#f7f7f7',
                    'border': '1px solid #e0e0e0',
                    'borderRadius': '5px',
                    'padding': '5px 14px',
                  }}>
                    <span style={{
                      'backgroundColor': '#21ba45',
                      'color': 'white',
                      'borderRadius': '5px',
                      'padding': '2px 8px',
                      'fontSize': '0.82em',
                      'fontWeight': '600',
                      'letterSpacing': '0.3px',
                    }}>
                      {tableState.filteredCount}
                    </span>
                    <span style={{
                      'color': '#555',
                      'fontSize': '0.85em',
                      'fontWeight': '500',
                    }}>
                      participantes
                    </span>
                  </div>
                  <Button
                    basic
                    color="blue"
                    size="small"
                    compact
                    onClick={() => setTableState((prev) => ({...prev, 'key': prev.key + 1}))}
                  >
                    <Icon name="cancel" /> Limpiar Filtros
                  </Button>
                </div>
              </Grid.Column>
              <Grid.Column width={2} textAlign="right">
                <WtsSkillsTechDetailsModal />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column floated="left" width={16}>
                <div className="table-sticky-actions">
                  <style>{`
                    .table-sticky-actions table thead th {
                      background-color: rgba(74, 144, 226, 0.5) !important;
                      color: #ffffff !important;
                    }
                    .table-sticky-actions table tbody tr:nth-child(odd) td {
                      background-color: #ffffff !important;
                    }
                    .table-sticky-actions table tbody tr:nth-child(even) td {
                      background-color: #e6f2ff !important;
                    }
                    .table-sticky-actions table th:last-child,
                    .table-sticky-actions table td:last-child {
                      position: sticky !important;
                      right: 0 !important;
                      z-index: 10 !important;
                      box-shadow: -3px 0px 5px -2px rgba(0,0,0,0.15) !important;
                    }
                    .table-sticky-actions table thead th:last-child {
                      background-color: rgba(74, 144, 226, 0.5) !important;
                      z-index: 11 !important;
                    }
                    .table-sticky-actions table tbody tr:nth-child(odd) td:last-child {
                      background-color: #ffffff !important;
                    }
                    .table-sticky-actions table tbody tr:nth-child(even) td:last-child {
                      background-color: #e6f2ff !important;
                    }
                  `}</style>
                  <CommercialTable
                    key={`${tableState.key}-${wtsEmployees.length}`}
                    columns={columns}
                    data={sortedEmployees}
                    defaultColumnFilter={DefaultColumnFilter}
                    defaultPagination={10}
                    setFilteredData={(rows) => setTableState((prev) => ({...prev, 'filteredCount': rows.length}))}
                    initialState={{
                      'filters': [
                        {
                          'id': 'status',
                          'value': 'ACTIVO',
                        },
                      ],
                    }}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}
      </Card>
    </Container>
  );
};

export default WtsEmployeesGestor;
